import React from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Building2, Check, ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import EnquiryForm from "@/components/EnquiryForm";
import ProjectCard from "@/components/ProjectCard";
import { WHATSAPP_URL } from "@/components/WhatsAppButton";
import { allProjects, opportunities, rakProjectFacts } from "@/data/projects";
import NotFound from "@/pages/NotFound";

const ProjectDetail = () => {
  const { slug } = useParams();
  const project = allProjects.find((p) => p.slug === slug);
  if (!project) return <NotFound />;

  const facts = rakProjectFacts[project.slug] || [];
  const isAlMarjan = project.location.includes("Al Marjan Island");
  const related = allProjects.filter((p) => p.slug !== slug && p.emirate === project.emirate).slice(0, 3);
  const isDev = typeof window !== "undefined" && window.location.pathname.startsWith("/developments");
  const isOpp = opportunities.some((o) => o.slug === slug);
  const canonicalPath = `/${isOpp ? "opportunities" : "developments"}/${slug}`;
  const canonicalParent = isOpp ? { name: "Opportunities", path: "/opportunities" } : { name: "Developments", path: "/developments" };
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Residence",
    name: project.name,
    description: project.tagline,
    address: { "@type": "PostalAddress", addressLocality: project.location, addressCountry: "AE" },
  };

  return (
    <div data-testid={`project-page-${slug}`}>
      <SEO
        title={project.seoTitle || `${project.name} — ${project.location}`}
        description={project.metaDescription || `${project.name}, ${project.location}. ${project.tagline} Curated by ACRE — Al Casa Real Estate. Price on request.`}
        path={canonicalPath}
        keywords={`${project.name}, ${project.type}, ${project.location} property`}
        jsonLd={jsonLd}
        breadcrumbs={[
          { name: "Home", path: "/" },
          canonicalParent,
          { name: project.name, path: canonicalPath },
        ]}
      />

      <section className="relative pt-32 pb-16 overflow-hidden" data-testid="project-hero">
        <img src={project.image} alt={`${project.name}, ${project.location}`} className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0C10]/70 via-[#0B0C10]/85 to-[#0B0C10]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <Link to={isDev ? "/developments" : "/opportunities"} data-testid="project-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#9DA3AE] hover:text-[#E6CA65] transition-colors mb-8">
            <ArrowLeft size={14} /> {isDev ? "All Developments" : "All Opportunities"}
          </Link>
          <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-semibold text-[#0B0C10] bg-gradient-to-r from-[#E6CA65] to-[#C5A880] px-3 py-1.5 rounded-full mb-5">
            {project.badge}
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light text-[#F4F3EF] leading-[1.08] max-w-4xl">{project.h1 || project.name}</h1>
          <p className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-6 text-sm text-[#9DA3AE]">
            <span className="inline-flex items-center gap-1.5"><MapPin size={14} className="text-[#C5A880]" /> {project.location}</span>
            <span className="inline-flex items-center gap-1.5"><Building2 size={14} className="text-[#C5A880]" /> {project.developer}</span>
            <span className="text-[#E6CA65]">{facts.length > 0 ? "Published facts below · Current availability on request" : "Price on Request"}</span>
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-3">
            <Reveal>
              <div className="rounded-2xl overflow-hidden border border-[rgba(197,168,128,0.2)] mb-10">
                <img src={project.image} alt={`${project.name} — ${project.type}`} className="w-full h-[420px] object-cover" />
              </div>
            </Reveal>
            <Reveal>
              <p className="eyebrow mb-4">Overview</p>
              <p className="font-display text-2xl sm:text-3xl text-[#F4F3EF] font-light leading-snug mb-8">{project.tagline}</p>
            </Reveal>
            {project.description.map((p, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <p className="text-[#9DA3AE] font-light leading-relaxed mb-6">{p}</p>
              </Reveal>
            ))}
            {facts.length > 0 && (
              <Reveal>
                <div className="mt-10 mb-10 bg-[#13151D] border border-[rgba(197,168,128,0.2)] rounded-2xl p-6" data-testid="project-published-facts">
                  <p className="eyebrow mb-5">Published project facts</p>
                  <div className="space-y-5">
                    {facts.map((fact) => (
                      <div key={fact.label} className="border-b border-[rgba(197,168,128,0.1)] pb-4 last:border-b-0 last:pb-0">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-[#C5A880] mb-1.5">{fact.label}</p>
                        <p className="text-sm text-[#F4F3EF] font-light">{fact.value}</p>
                        <p className="text-[11px] text-[#6b7280] font-light mt-1.5">{fact.source}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-[#6b7280] font-light mt-5 pt-4 border-t border-[rgba(197,168,128,0.12)]">
                    Prices are published entry or asking figures as labelled, not valuations or guarantees of availability. Payment plans and handover dates are subject to the reservation agreement and SPA.
                  </p>
                </div>
              </Reveal>
            )}
            <Reveal>
              <p className="eyebrow mb-5 mt-10">Highlights</p>
              <ul className="grid sm:grid-cols-2 gap-4">
                {project.highlights.map((h) => (
                  <li key={h} className="flex gap-3 text-sm text-[#c9cdd6] font-light bg-[#13151D] border border-[rgba(197,168,128,0.12)] rounded-xl px-5 py-4">
                    <Check size={16} className="text-[#E6CA65] shrink-0 mt-0.5" /> {h}
                  </li>
                ))}
              </ul>
            </Reveal>
            {project.emirate === "Ras Al Khaimah" && (
              <Reveal>
                <div className="mt-10 bg-[#13151D] border border-[rgba(197,168,128,0.2)] rounded-2xl p-6" data-testid="rak-context-links">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#C5A880] mb-4">
                    {isAlMarjan ? "Researching Al Marjan Island?" : "Researching Ras Al Khaimah?"}
                  </p>
                  <div className="flex flex-wrap gap-x-6 gap-y-2.5 text-sm">
                    {isAlMarjan ? (
                      <>
                        <Link to="/destinations/ras-al-khaimah" className="text-[#E6CA65] underline underline-offset-4 decoration-[rgba(230,202,101,0.4)] hover:decoration-[#E6CA65] transition-colors">Explore Al Marjan Island property</Link>
                        <Link to="/insights/al-marjan-island-project-comparison" className="text-[#E6CA65] underline underline-offset-4 decoration-[rgba(230,202,101,0.4)] hover:decoration-[#E6CA65] transition-colors">Compare Al Marjan projects</Link>
                        <Link to="/insights/al-marjan-island-property-prices" className="text-[#E6CA65] underline underline-offset-4 decoration-[rgba(230,202,101,0.4)] hover:decoration-[#E6CA65] transition-colors">Read the Al Marjan price guide</Link>
                      </>
                    ) : (
                      <Link to="/destinations/ras-al-khaimah" className="text-[#E6CA65] underline underline-offset-4 decoration-[rgba(230,202,101,0.4)] hover:decoration-[#E6CA65] transition-colors">Explore Ras Al Khaimah</Link>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3 mt-5 pt-5 border-t border-[rgba(197,168,128,0.12)]">
                    <a href="#enquire" data-testid="project-cta-brochure" className="text-[11px] uppercase tracking-[0.14em] font-semibold px-5 py-2.5 rounded-full text-[#0B0C10] bg-gradient-to-r from-[#E6CA65] to-[#C5A880]">Request Brochure</a>
                    <a href="#enquire" data-testid="project-cta-availability" className="text-[11px] uppercase tracking-[0.14em] font-semibold px-5 py-2.5 rounded-full border border-[rgba(197,168,128,0.4)] text-[#E6CA65] hover:bg-[rgba(197,168,128,0.08)] transition-colors">Request Current Availability</a>
                    <a href="#enquire" data-testid="project-cta-payment-plan" className="text-[11px] uppercase tracking-[0.14em] font-semibold px-5 py-2.5 rounded-full border border-[rgba(197,168,128,0.4)] text-[#E6CA65] hover:bg-[rgba(197,168,128,0.08)] transition-colors">View Payment Plan</a>
                    <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="project-cta-whatsapp" className="text-[11px] uppercase tracking-[0.14em] font-semibold px-5 py-2.5 rounded-full border border-[rgba(197,168,128,0.4)] text-[#E6CA65] hover:bg-[rgba(197,168,128,0.08)] transition-colors">WhatsApp ACRE</a>
                  </div>
                </div>
              </Reveal>
            )}
            <p className="text-[11px] text-[#6b7280] font-light mt-10 leading-relaxed">
              Pricing, availability and payment plans are subject to change and confirmed on enquiry. Information is for
              general guidance only and does not constitute financial advice.
            </p>
          </div>
          <div className="lg:col-span-2">
            <div className="lg:sticky lg:top-28" id="enquire">
              <EnquiryForm projectName={project.name} sourcePage={`/opportunities/${slug}`} />
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="pb-24" data-testid="project-related">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <Reveal>
              <h2 className="font-display text-3xl font-light text-[#F4F3EF] mb-10">Also in {project.emirate}</h2>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((p, i) => (
                <Reveal key={p.slug} delay={i * 0.08}>
                  <ProjectCard project={p} index={i} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ProjectDetail;
